import { type CognitoUser, type CognitoUserSession } from 'amazon-cognito-identity-js';
import { type JwtPayload, jwtDecode } from 'jwt-decode';

import { Auth } from '@air/api';

const FORCE_REFRESH_THRESHOLD_SECONDS = 90; // API requires >1 min remaining; 90s provides buffer

const getSession = (cognitoUser: CognitoUser): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    cognitoUser.getSession((error: Error | null, session: CognitoUserSession | null) =>
      error || !session ? reject(error || new Error('No session available')) : resolve(session),
    );
  });

const forceRefreshSession = (
  cognitoUser: CognitoUser,
  currentSession: CognitoUserSession,
): Promise<CognitoUserSession> =>
  new Promise((resolve, reject) => {
    const refreshToken = currentSession.getRefreshToken();
    cognitoUser.refreshSession(refreshToken, (error, session) =>
      error || !session ? reject(error || new Error('Refresh failed')) : resolve(session),
    );
  });

const shouldForceRefresh = (session: CognitoUserSession): boolean => {
  try {
    const token = session.getAccessToken().getJwtToken();
    const payload = jwtDecode<JwtPayload>(token);

    if (!payload.exp) {
      return true;
    }

    const now = Math.floor(Date.now() / 1000);
    const expiresIn = payload.exp - now;

    return expiresIn <= FORCE_REFRESH_THRESHOLD_SECONDS;
  } catch {
    return true; // Force refresh on decode error
  }
};

/**
 * Gets a valid session, force-refreshing if the token is close to expiring.
 * API requires tokens with >1 minute remaining, so we refresh at 90 seconds.
 */
const getValidSession = async (cognitoUser: CognitoUser): Promise<CognitoUserSession> => {
  const session = await getSession(cognitoUser);

  if (shouldForceRefresh(session)) {
    return forceRefreshSession(cognitoUser, session);
  }

  return session;
};

/**
 * Fetches a valid auth token from Cognito.
 *
 * Normal calls use the cached session (with automatic refresh if near expiry).
 * When `forceRefresh` is true, the session is always refreshed to get a brand-new token.
 */
export const fetchAuthToken = async (options?: { forceRefresh?: boolean }): Promise<string> => {
  const cognitoUser = await Auth.getCurrentSession();

  if (options?.forceRefresh) {
    const session = await getSession(cognitoUser);
    const refreshedSession = await forceRefreshSession(cognitoUser, session);
    return refreshedSession.getAccessToken().getJwtToken();
  }

  const session = await getValidSession(cognitoUser);
  return session.getAccessToken().getJwtToken();
};
